import { GenericAbortSignal } from 'axios';
import { http } from '@/api/http';
import { ApiResponseBody, PaginatedApiResponseBody, PaginationSearchParams, Uuid } from '@/api/types';
import {
  AircraftAvailabilityView,
  AircraftView,
  Driver,
  BannedTailNumber,
  Employee,
  EmployeeAvailabilityData,
  EmployeeRestPeriod,
  EmployeeRestPeriodModifyRequestBody,
  EmployeesReservationsData,
  EmployeesReservationsParams,
  getResourcesApiUrl,
  DriverFormType,
  BannedTailNumberFormType,
  OcsConsoleFormType,
  OcsConsoleData,
  PaginatedDriverListFilters,
  PaginatedEmployeesListFilters,
  PaginatedOcsConsoleListFilters,
  OcsConsoleByIdParams,
  PaginatedPerfusionSetListFilters,
  PerfusionSet,
  ResourcesAvailabilityData,
  ResourcesAvailabilityParams,
  AssignmentCSCandidatesParams,
  OMSAssignmentCandidate,
  CSAssignmentCandidate,
  RSAssignmentCandidate,
  AssignmentCandidateParams,
  RSContractorAssignmentCandidate,
  ReliefAssignmentCSCandidateParams,
  CCAssignmentCandidate,
  CLAssignmentCandidate,
} from '@/api/apis/resources';
import { EmployeeFormType, OscConsoleUpdateType } from '@/types';
import { OrganType } from '../case';

const getEmployeeList = async (params: PaginatedEmployeesListFilters) => {
  const res = await http.get<PaginatedApiResponseBody<Employee>>(getResourcesApiUrl.employees(), { params });
  return res.data.data;
};

const getEmployeeById = async (employeeId: string) => {
  const res = await http.get<ApiResponseBody<Employee>>(getResourcesApiUrl.employeeById(employeeId));
  return res.data.data;
};

const getEmployeeByUserId = async (employeeId: string) => {
  const res = await http.get<ApiResponseBody<Employee>>(getResourcesApiUrl.employeeByUserId(employeeId));
  return res.data.data;
};

const updateEmployeeRequestFactory =
  (urlGetter: (id: string) => string) => async (body: Partial<EmployeeFormType> & { id: string }) => {
    const res = await http.patch<ApiResponseBody<Employee>>(urlGetter(body.id), body);
    return res.data.data;
  };

const updateEmployee = updateEmployeeRequestFactory((id: string) => getResourcesApiUrl.employeeById(id));
const updateCSEmployee = updateEmployeeRequestFactory((id: string) => getResourcesApiUrl.csById(id));
const updateCLEmployee = updateEmployeeRequestFactory((id: string) => getResourcesApiUrl.clById(id));

const updateProfile = async (body: Partial<EmployeeFormType>) => {
  const res = await http.patch<ApiResponseBody<Employee>>(getResourcesApiUrl.employees(), body);
  return res.data.data;
};

const changeEmployeesActiveStateFactory = (urlGetter: (id: string) => string) => async (employeeId: string) => {
  const res = await http.post<ApiResponseBody<Employee>>(urlGetter(employeeId));
  return res.data.data;
};

const activateEmployee = changeEmployeesActiveStateFactory((id: string) => getResourcesApiUrl.activateEmployeeById(id));
const activateCSEmployee = changeEmployeesActiveStateFactory((id: string) => getResourcesApiUrl.activateCSById(id));
const activateCLEmployee = changeEmployeesActiveStateFactory((id: string) => getResourcesApiUrl.activateCLById(id));
const deactivateEmployee = changeEmployeesActiveStateFactory((id: string) =>
  getResourcesApiUrl.deactivateEmployeeById(id),
);
const deactivateCSEmployee = changeEmployeesActiveStateFactory((id: string) => getResourcesApiUrl.deactivateCSById(id));
const deactivateCLEmployee = changeEmployeesActiveStateFactory((id: string) => getResourcesApiUrl.deactivateCLById(id));

const modifyEmployeeRestPeriodFactory = (urlGetter: () => string) => {
  return async (body: EmployeeRestPeriodModifyRequestBody) => {
    const res = await http.post<ApiResponseBody<EmployeeRestPeriod>>(urlGetter(), body);
    return res.data.data;
  };
};

const modifyCSRestPeriod = modifyEmployeeRestPeriodFactory(() => getResourcesApiUrl.csRestPeriodsModify());
const modifyRSLiRestPeriod = modifyEmployeeRestPeriodFactory(() => getResourcesApiUrl.rsLiRestPeriodsModify());
const modifyRSHtLuRestPeriod = modifyEmployeeRestPeriodFactory(() => getResourcesApiUrl.rsHtLuRestPeriodsModify());

const getOcsConsolesList = async (params: PaginatedOcsConsoleListFilters) => {
  const res = await http.get<PaginatedApiResponseBody<OcsConsoleData>>(getResourcesApiUrl.ocsConsoles(), {
    params,
  });
  return res.data.data;
};

const getOcsConsoleById = async (consoleId: string, params?: OcsConsoleByIdParams) => {
  const res = await http.get<ApiResponseBody<OcsConsoleData>>(getResourcesApiUrl.ocsConsoleById(consoleId), {
    params,
  });
  return res.data.data;
};

const createOcsConsole = async (body: Partial<OcsConsoleFormType>) => {
  const res = await http.post<ApiResponseBody<OcsConsoleData>>(getResourcesApiUrl.ocsConsoles(), body);
  return res.data.data;
};

const updateOcsConsole = async ({ id, ...body }: Partial<OscConsoleUpdateType> & { id: string }) => {
  const res = await http.patch<ApiResponseBody<OcsConsoleData>>(getResourcesApiUrl.ocsConsoleById(id), body);
  return res.data.data;
};

const removeOcsConsole = async (consoleId: string) => {
  const res = await http.delete<{ message: string }>(getResourcesApiUrl.ocsConsoleById(consoleId));
  return res.data;
};

const getPerfusionSetsList = async (params: PaginatedPerfusionSetListFilters) => {
  const res = await http.get<PaginatedApiResponseBody<PerfusionSet>>(getResourcesApiUrl.inventory(), {
    params,
  });
  return res.data.data;
};

const copyInventory = async (fromCaseId: Uuid, toCaseId: Uuid) => {
  const res = await http.post<{ message: string }>(getResourcesApiUrl.copyInventory(), { fromCaseId, toCaseId });
  return res.data;
};

const getResourcesAvailability = async (params: ResourcesAvailabilityParams) => {
  const res = await http.get<ApiResponseBody<ResourcesAvailabilityData>>(getResourcesApiUrl.resourcesAvailability(), {
    params,
  });
  return res.data.data;
};

const getAircraftResourcesAvailability = async (params: ResourcesAvailabilityParams, signal?: GenericAbortSignal) => {
  const res = await http.get<ApiResponseBody<AircraftAvailabilityView[]>>(getResourcesApiUrl.aircraftAvailability(), {
    params,
    signal,
  });
  return res.data.data;
};

const getEmployeeResourcesAvailability = async (
  params: Partial<ResourcesAvailabilityParams>,
  signal?: GenericAbortSignal,
) => {
  const res = await http.get<PaginatedApiResponseBody<EmployeeAvailabilityData>>(
    getResourcesApiUrl.employeeAvailability(),
    {
      params,
      signal,
    },
  );
  return res.data.data;
};

const getDeviceResourcesAvailability = async (params: ResourcesAvailabilityParams, signal: GenericAbortSignal) => {
  const res = await http.get<ApiResponseBody<OcsConsoleData[]>>(getResourcesApiUrl.ocsDeviceAvailability(), {
    params,
    signal,
  });
  return res.data.data;
};

const getAircraftsList = async (params: PaginationSearchParams & { ids: string }) => {
  const res = await http.get<PaginatedApiResponseBody<AircraftView>>(getResourcesApiUrl.aircrafts(), {
    params,
  });

  return res.data.data;
};

const getEmployeesReservations = async (params: EmployeesReservationsParams) => {
  const res = await http.get<ApiResponseBody<EmployeesReservationsData>>(getResourcesApiUrl.employeesReservations(), {
    params,
    paramsSerializer: { indexes: null },
  });

  return res.data.data;
};

const getCsCandidatesForAssignment = async (params: AssignmentCSCandidatesParams, signal: GenericAbortSignal) => {
  const res = await http.get<ApiResponseBody<CSAssignmentCandidate[]>>(
    getResourcesApiUrl.clinicalSpecialistsForAssignment(),
    {
      params,
      signal,
    },
  );

  return res.data.data;
};

const getReliefCsCandidatesForAssignment = async (
  params: ReliefAssignmentCSCandidateParams,
  signal: GenericAbortSignal,
) => {
  const res = await http.get<ApiResponseBody<CSAssignmentCandidate[]>>(
    getResourcesApiUrl.reliefSpecialistsForAssignment(),
    {
      params,
      signal,
    },
  );

  return res.data.data;
};

const getSurgeonCandidatesForAssignment = async (params: AssignmentCandidateParams, signal: GenericAbortSignal) => {
  const res = await http.get<ApiResponseBody<RSAssignmentCandidate[]>>(getResourcesApiUrl.surgeonsForAssignment(), {
    params,
    signal,
  });

  return res.data.data;
};

const getContractorSurgeonCandidatesForAssignment = async (
  params: AssignmentCandidateParams,
  signal: GenericAbortSignal,
) => {
  const res = await http.get<ApiResponseBody<RSContractorAssignmentCandidate[]>>(
    getResourcesApiUrl.contractorSurgeonsForAssignment(),
    {
      params,
      signal,
    },
  );

  return res.data.data;
};

const getCoordinatorCandidatesForAssignment = async (assignedEmployeeIds: Uuid[], signal: GenericAbortSignal) => {
  const res = await http.get<ApiResponseBody<CCAssignmentCandidate[]>>(getResourcesApiUrl.coordinatorsForAssignment(), {
    signal,
    params: { assignedEmployeeIds },
    paramsSerializer: { indexes: null },
  });

  return res.data.data;
};

const getOMSCandidatesForAssignment = async (assignedEmployeeIds: Uuid[], signal: GenericAbortSignal) => {
  const res = await http.get<ApiResponseBody<OMSAssignmentCandidate[]>>(getResourcesApiUrl.omsForAssignment(), {
    signal,
    params: { assignedEmployeeIds },
    paramsSerializer: { indexes: null },
  });

  return res.data.data;
};

const getClinicalLeadsForAssignment = async (
  assignedEmployeeIds: Uuid[],
  organ: OrganType,
  signal: GenericAbortSignal,
) => {
  const res = await http.get<ApiResponseBody<CLAssignmentCandidate[]>>(
    getResourcesApiUrl.clinicalLeadsForAssignment(),
    {
      signal,
      params: { organ, assignedEmployeeIds },
      paramsSerializer: { indexes: null },
    },
  );

  return res.data.data;
};

const createBannedTailNumber = async (body: Partial<BannedTailNumberFormType>) => {
  const res = await http.post<ApiResponseBody<BannedTailNumberFormType>>(getResourcesApiUrl.bannedTailNumbers(), body);
  return res.data.data;
};

const getBannedTailNumbersList = async (params: PaginationSearchParams) => {
  const res = await http.get<PaginatedApiResponseBody<BannedTailNumber>>(getResourcesApiUrl.bannedTailNumbers(), {
    params,
  });
  return res.data.data;
};

const getBannedTailNumbersById = async (bannedTailNumberId: string) => {
  const res = await http.get<ApiResponseBody<BannedTailNumber>>(
    getResourcesApiUrl.bannedTailNumbersById(bannedTailNumberId),
  );
  return res.data.data;
};

const updateBannedTailNumber = async ({ id, ...body }: Partial<BannedTailNumberFormType> & { id: string }) => {
  const res = await http.patch<ApiResponseBody<BannedTailNumber>>(getResourcesApiUrl.bannedTailNumbersById(id), body);
  return res.data.data;
};

const removeBannedTailNumber = async (bannedTailNumberId: string) => {
  const res = await http.delete<{ message: string }>(getResourcesApiUrl.bannedTailNumbersById(bannedTailNumberId));
  return res.data;
};

const createBannedDriver = async (body: Partial<DriverFormType>) => {
  const res = await http.post<ApiResponseBody<DriverFormType>>(getResourcesApiUrl.drivers(), body);
  return res.data.data;
};

const getBannedDriversList = async (params: PaginatedDriverListFilters) => {
  const res = await http.get<PaginatedApiResponseBody<Driver>>(getResourcesApiUrl.drivers(), {
    params,
  });
  return res.data.data;
};

const getBannedDriversById = async (bannedDriverId: string) => {
  const res = await http.get<ApiResponseBody<Driver>>(getResourcesApiUrl.driverById(bannedDriverId));
  return res.data.data;
};

const updateBannedDriver = async ({ id, ...body }: Partial<DriverFormType> & { id: string }) => {
  const res = await http.patch<ApiResponseBody<Driver>>(getResourcesApiUrl.driverById(id), body);
  return res.data.data;
};

const removeBannedDriver = async (bannedDriverId: string) => {
  const res = await http.delete<{ message: string }>(getResourcesApiUrl.driverById(bannedDriverId));
  return res.data;
};

export const resourceApiFetcher = {
  getEmployeeList,
  getEmployeeById,
  getEmployeeByUserId,
  updateEmployee,
  updateCSEmployee,
  updateCLEmployee,
  updateProfile,
  activateEmployee,
  activateCSEmployee,
  activateCLEmployee,
  deactivateEmployee,
  deactivateCSEmployee,
  deactivateCLEmployee,
  modifyCSRestPeriod,
  modifyRSLiRestPeriod,
  modifyRSHtLuRestPeriod,
  getOcsConsolesList,
  getOcsConsoleById,
  createOcsConsole,
  updateOcsConsole,
  removeOcsConsole,
  getPerfusionSetsList,
  copyInventory,
  getResourcesAvailability,
  getAircraftsList,
  getAircraftResourcesAvailability,
  getEmployeeResourcesAvailability,
  getDeviceResourcesAvailability,
  getEmployeesReservations,
  createBannedTailNumber,
  getBannedTailNumbersList,
  getBannedTailNumbersById,
  updateBannedTailNumber,
  removeBannedTailNumber,
  createBannedDriver,
  getBannedDriversList,
  getBannedDriversById,
  updateBannedDriver,
  removeBannedDriver,
  getCsCandidatesForAssignment,
  getReliefCsCandidatesForAssignment,
  getSurgeonCandidatesForAssignment,
  getContractorSurgeonCandidatesForAssignment,
  getCoordinatorCandidatesForAssignment,
  getOMSCandidatesForAssignment,
  getClinicalLeadsForAssignment,
};
